import { DatePicker, Input, Table, Tag, Button, Pagination } from 'antd'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import moment from 'moment'
import React, { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory, useLocation } from 'react-router-dom'
import HeaderScreen from 'src/components/shared/header-screen/HeaderScreen'
import { CSVLink } from 'react-csv'
import { buildCsvString } from 'src/utils/csv'
import FadeIn from 'src/components/styled/FadeIn'
import {
  fetchAdminOrders,
  fetchSupplierPortalOrders,
} from 'src/features/AdminOrderSlice'
import InviteCustomerPrompt from 'src/screens/overview/customers/InviteCustomerPrompt'
import useLunrSearch from 'src/utils/useLunrSearch'
import styled from 'styled-components/macro'
import { dateRangeCaption } from 'src/utils/dateRangeCaption'

const AdminOrders = () => {
  const { Search } = Input
  const dispatch = useDispatch()
  const history = useHistory()
  const location = useLocation()
  const weeklyDate = [moment().subtract(7, 'days'), moment()]
  const dateFormat = 'YYYY-MM-DD'
  const isSupplierOrders = location.pathname.startsWith('/supplier')

  const allAdminOrders = useSelector(state => state.adminOrders.adminOrders)
  const isLoading = useSelector(state => state.adminOrders.isLoading)
  const pagination = useSelector(state => state.adminOrders.pagination)
  const hasIntegratedSuppliers = useSelector(
    state => state.adminOrders.hasIntegratedSuppliers,
  )
  const supplierId = useSelector(
    state => state.auth.account?.supplier?.[0]?._id,
  )

  const [searchTerm, setSearchTerm] = useState('')
  const [searchVenueOrders, setSearchVenueOrders] = useState([])
  const [selectedRange, setSelectedRange] = useState({
    from: weeklyDate[0].format(dateFormat),
    to: weeklyDate[1].format(dateFormat),
  })

  useEffect(() => {
    dispatch(
      (isSupplierOrders ? fetchSupplierPortalOrders : fetchAdminOrders)(
        selectedRange.from,
        selectedRange.to,
        1,
        50,
      ),
    )
  }, [dispatch])

  const onDateSelection = (value, dateString) => {
    setSelectedRange({ from: dateString[0], to: dateString[1] })
    dispatch(
      (isSupplierOrders ? fetchSupplierPortalOrders : fetchAdminOrders)(
        dateString[0],
        dateString[1],
        1,
        pagination.limit,
        searchTerm,
      ),
    )
  }

  const handlePageChange = (page, pageSize) => {
    dispatch(
      (isSupplierOrders ? fetchSupplierPortalOrders : fetchAdminOrders)(
        selectedRange.from,
        selectedRange.to,
        page,
        pageSize || pagination.limit,
        searchTerm,
      ),
    )
  }

  dayjs.extend(utc)
  const resetPagination = useCallback(() => {}, [])

  useLunrSearch(
    allAdminOrders,
    ['supplierTitle', 'venueTitle', 'status'],
    isSupplierOrders ? '' : searchTerm,
    setSearchVenueOrders,
    resetPagination,
  )

  const disableEndDate = current => current.valueOf() > moment.now()

  const stringSort = (a, b) => String(a).trim().localeCompare(String(b).trim())

  // Chunk L4 — bulk contacts export control removed from UI
  // (same hygiene as Spartacus CSV). API left unchanged.

  // build CSV rows
  const csvHeaders = [
    'Venue',
    'Time',
    'Date',
    'Total',
    'Supplier',
    'Second title',
    'Account number',
    'Status',
    'Order Source',
  ]

  const csvRows = searchVenueOrders.map(item => [
    item.venueTitle || '',
    dayjs(item.date).format('hh.mma'),
    dayjs.utc(item.date).format('DD/MM/YY'),
    item.price ? Number(item.price / 100).toFixed(2) : '',
    item.supplierTitle || '',
    item.supplierSecondTitle || '',
    item.accountNumber || '',
    item.status || '',
    item.placedVia === 'Order placed manually on the dashboard'
      ? 'Order Placed Outside OMS'
      : 'OMS Order',
  ])

  const openInviteFlow = () => {
    history.push('/supplier/customers', { openInvite: true })
  }

  const viewCustomerProgress = () => {
    history.push('/supplier/customers')
  }

  return (
    <Wrapper>
      <HeaderScreen title={'Orders'}>
        <div style={{ display: 'flex', gap: '10px' }}>
          <Button type={'primary'} style={{ width: 160, height: 40 }}>
            <CSVLink
              data={buildCsvString([csvHeaders, ...csvRows])}
              filename={`Admin_Orders.csv`}
            >
              Download CSV
            </CSVLink>
          </Button>
        </div>
      </HeaderScreen>
      {isSupplierOrders && (
        <InviteCustomerPrompt
          supplierId={supplierId}
          onInvite={openInviteFlow}
          onViewProgress={viewCustomerProgress}
        />
      )}
      <SearchFilterBlock>
        <DateControl>
          <DateCaption>
            {dateRangeCaption(selectedRange.from, selectedRange.to)}
          </DateCaption>
          <StyledRangePicker
            disabledDate={disableEndDate}
            format={dateFormat}
            defaultValue={weeklyDate}
            onChange={onDateSelection}
          />
        </DateControl>
        <Search
          size='large'
          placeholder={
            isSupplierOrders
              ? hasIntegratedSuppliers
                ? 'Search PO, account number or filename'
                : 'Search PO or account number'
              : 'Search'
          }
          onSearch={value => {
            setSearchTerm(value)
            if (isSupplierOrders) {
              dispatch(
                fetchSupplierPortalOrders(
                  selectedRange.from,
                  selectedRange.to,
                  1,
                  pagination.limit,
                  value,
                ),
              )
            }
          }}
          style={{ width: 300 }}
          allowClear
        />
      </SearchFilterBlock>
      {/* Total count display */}
      <div style={{ margin: '8px 0' }}>
        {`Total: ${pagination.total} orders`}
      </div>
      <Table
        scroll={{
          scrollToFirstRowOnChange: true,
          ...(isSupplierOrders ? { x: 1100 } : { x: false }),
        }}
        dataSource={searchVenueOrders.map(data => ({
          ...data,
          key: data._id,
        }))}
        loading={isLoading}
        pagination={{
          current: pagination.page,
          pageSize: pagination.limit,
          total: pagination.total,
          onChange: handlePageChange,
          onShowSizeChange: handlePageChange,
          showSizeChanger: pagination.total > 50,
          pageSizeOptions: ['25', '50', '100', '200'],
          position: ['bottomCenter'],
          hideOnSinglePage: true,
          showTotal: (total, range) =>
            `Showing ${range[0]}-${range[1]} of ${total} orders`,
        }}
        columns={[
          {
            title: 'Venue',
            dataIndex: 'venueTitle',
            key: 'venueTitle',
            className: 'restrictShrink',
            sorter: (a, b) => stringSort(a.venueTitle, b.venueTitle),
            sortDirections: ['ascend', 'descend'],
          },
          {
            title: 'Time',
            dataIndex: 'date',
            key: 'time',
            className: 'restrictShrink',
            render: time => dayjs(time).format('hh.mma'),
          },
          {
            title: 'Date',
            dataIndex: 'date',
            key: 'date',
            className: 'restrictShrink',
            render: date => dayjs.utc(date).format('DD/MM/YY'),
          },
          {
            title: 'Total',
            dataIndex: 'price',
            key: 'price',
            className: 'restrictShrink',
            render: text => {
              return text ? `£${Number(text / 100).toFixed(2)}` : ``
            },
          },
          {
            title: 'Supplier',
            dataIndex: 'supplierTitle',
            key: 'supplierTitle',
            className: 'restrictShrink',
            sorter: (a, b) => stringSort(a.supplierTitle, b.supplierTitle),
            sortDirections: ['ascend', 'descend'],
          },
          {
            title: 'Second title',
            dataIndex: 'supplierSecondTitle',
            key: 'supplierSecondTitle',
            className: 'restrictShrink',
            sorter: (a, b) =>
              stringSort(a.supplierSecondTitle, b.supplierSecondTitle),
            sortDirections: ['ascend', 'descend'],
          },
          {
            title: 'Account number',
            dataIndex: 'accountNumber',
            key: 'accountNumber',
            className: 'restrictShrink',
            sorter: (a, b) => stringSort(a.accountNumber, b.accountNumber),
            sortDirections: ['ascend', 'descend'],
          },
          {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            className: 'restrictShrink',
            sorter: (a, b) => stringSort(a.status, b.status),
            sortDirections: ['ascend', 'descend'],
            render: status => (
              <Tag
                color={
                  status === 'Approved'
                    ? 'green'
                    : status === 'Rejected'
                    ? 'magenta'
                    : 'orange'
                }
              >
                {status}
              </Tag>
            ),
          },
          {
            title: 'Checklist',
            dataIndex: 'venueId',
            key: 'venueId',
            className: 'restrictShrink',
            render: (a, item) =>
              item && (
                <Action>
                  <a
                    target='_blank'
                    rel='noopener noreferrer'
                    style={{
                      color: 'DodgerBlue',
                      textDecorationLine: 'underline',
                    }}
                    href={`/checklist/${item.venueId}/${item.supplierId}/${dayjs
                      .utc(item.date)
                      .format('YYYY-MM-DD')}/${
                      item.purchaseOrderNumber
                        ? encodeURIComponent(item.purchaseOrderNumber)
                        : 'all'
                    }?admin=true`}
                  >
                    Checklist
                  </a>
                  {item.placedVia ===
                  'Order placed manually on the dashboard' ? (
                    <Placed>This order was placed outside of OMS</Placed>
                  ) : (
                    ''
                  )}
                </Action>
              ),
          },
        ]}
      />
      {/* External pagination to guarantee visibility */}
      <div style={{ display: 'flex', justifyContent: 'center', marginTop: 16 }}>
        <Pagination
          current={pagination.page}
          pageSize={pagination.limit}
          total={pagination.total}
          onChange={handlePageChange}
          onShowSizeChange={handlePageChange}
          showSizeChanger={pagination.total > 50}
          pageSizeOptions={['25', '50', '100', '200']}
          showTotal={(total, range) =>
            `Showing ${range[0]}-${range[1]} of ${total} orders`
          }
          hideOnSinglePage
        />
      </div>
    </Wrapper>
  )
}
const Wrapper = styled(FadeIn)`
  width: 100%;
  min-width: 0;

  .restrictShrink {
    min-width: 95px;
    white-space: nowrap;
  }

  .ant-table-thead > tr > th {
    white-space: nowrap;
  }

  .ant-table-wrapper,
  .ant-spin-nested-loading {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }
`

const Action = styled.div`
  position: relative;
`

const DateControl = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const DateCaption = styled.span`
  font-weight: 600;
  white-space: nowrap;
`

const StyledRangePicker = styled(DatePicker.RangePicker)`
  .ant-calendar-picker-input.ant-input {
    height: 40px;
    padding: 6px 6px;
    font-size: 16px;
    width: 280px;
  }
  .ant-calendar-range-picker-input {
    width: 42%;
  }
`

const SearchFilterBlock = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;

  @media (max-width: 890px) {
    flex-direction: column;
    align-items: stretch;

    .ant-calendar-picker,
    .ant-input-search {
      width: 100% !important;
      max-width: 100%;
    }
  }
`

const Placed = styled.div`
  position: absolute;
  white-space: nowrap;
  width: auto;
  left: -85px;
  bottom: -18px;
  font-size: 14px;
  text-align: center;
  color: ${({ theme }) => `${theme.colours.lettuceGreen}`};
`

export default AdminOrders
